// src/sectors/pilates/members/modal/detail/tabs/ScheduleTab.jsx
import React, { useEffect, useMemo, useState } from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Divider from '@mui/material/Divider';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import { SoftCard } from 'shared/ui/soft/SoftCard';
import { TabPanel } from 'shared/ui/soft/TabPanel';
import { PillIcon } from 'shared/ui/soft/PillIcon';

import { sessionsApi } from 'sectors/pilates/sessions/api/sessionsApi';

// ---------- utils ----------
function hmFromMin(min) {
  const v = Number(min);
  if (!Number.isFinite(v)) return '—';
  const hh = String(Math.floor(v / 60)).padStart(2, '0');
  const mm = String(v % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

const DAY_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cts', 'Paz'];
function dayLabelTR(dayISO) {
  if (!dayISO) return '—';
  const d = new Date(`${dayISO}T00:00:00`);
  const idx = (d.getDay() + 6) % 7;
  return DAY_TR[idx] || '—';
}

function monthKey(dayISO) {
  if (!dayISO || String(dayISO).length < 7) return '—';
  return String(dayISO).slice(0, 7);
}

function monthLabelTR(key) {
  if (!key || key === '—') return '—';
  const [y, m] = key.split('-').map((x) => Number(x));
  const months = [
    'Ocak',
    'Şubat',
    'Mart',
    'Nisan',
    'Mayıs',
    'Haziran',
    'Temmuz',
    'Ağustos',
    'Eylül',
    'Ekim',
    'Kasım',
    'Aralık',
  ];
  const mm = months[(m || 1) - 1] || '—';
  return `${mm} ${y || ''}`.trim();
}

function todayISO_TR() {
  // TR timezone için basit: UI tarafı sadece gruplama/filtre; backend zaten doğru.
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ---------- meta chips ----------
function sessionStatusMeta(status, theme, isDark) {
  const s = String(status || '').toUpperCase();
  if (s === 'CANCELLED') {
    return {
      label: 'İptal',
      icon: 'cancel',
      bg: alpha(theme.palette.error.main, isDark ? 0.16 : 0.1),
      bd: alpha(theme.palette.error.main, 0.2),
      fg: theme.palette.error.main,
    };
  }
  if (s === 'APPROVED') {
    return {
      label: 'Onay',
      icon: 'verified',
      bg: alpha(theme.palette.success.main, isDark ? 0.16 : 0.1),
      bd: alpha(theme.palette.success.main, 0.2),
      fg: theme.palette.success.main,
    };
  }
  if (s === 'PENDING') {
    return {
      label: 'Bekliyor',
      icon: 'hourglass_top',
      bg: alpha(theme.palette.info.main, isDark ? 0.14 : 0.1),
      bd: alpha(theme.palette.info.main, 0.18),
      fg: theme.palette.info.main,
    };
  }
  return {
    label: s || '—',
    icon: 'info',
    bg: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.06),
    bd: alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1),
    fg: theme.palette.text.primary,
  };
}

function attendanceMeta(status, theme, isDark) {
  const s = String(status || '').toUpperCase();
  if (s === 'ATTENDED') {
    return {
      label: 'Katıldı',
      icon: 'check_circle',
      bg: alpha(theme.palette.success.main, isDark ? 0.18 : 0.12),
      bd: alpha(theme.palette.success.main, 0.22),
      fg: theme.palette.success.main,
    };
  }
  if (s === 'NO_SHOW') {
    return {
      label: 'Gelmedi',
      icon: 'event_busy',
      bg: alpha(theme.palette.warning.main, isDark ? 0.18 : 0.12),
      bd: alpha(theme.palette.warning.main, 0.22),
      fg: theme.palette.warning.main,
    };
  }
  if (s === 'BURNED') {
    return {
      label: 'Yandı',
      icon: 'local_fire_department',
      bg: alpha(theme.palette.error.main, isDark ? 0.18 : 0.12),
      bd: alpha(theme.palette.error.main, 0.22),
      fg: theme.palette.error.main,
    };
  }
  if (s === 'CANCELLED') {
    return {
      label: 'İptal',
      icon: 'cancel',
      bg: alpha(theme.palette.error.main, isDark ? 0.14 : 0.1),
      bd: alpha(theme.palette.error.main, 0.18),
      fg: theme.palette.error.main,
    };
  }
  if (s === 'PENDING') {
    return {
      label: 'Bekliyor',
      icon: 'hourglass_top',
      bg: alpha(theme.palette.info.main, isDark ? 0.16 : 0.1),
      bd: alpha(theme.palette.info.main, 0.18),
      fg: theme.palette.info.main,
    };
  }
  return {
    label: s || '—',
    icon: 'help',
    bg: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.06),
    bd: alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1),
    fg: theme.palette.text.primary,
  };
}

function smallPill(theme, isDark, text, tone = 'text') {
  const pal =
    tone === 'info'
      ? theme.palette.info
      : tone === 'success'
      ? theme.palette.success
      : tone === 'warning'
      ? theme.palette.warning
      : tone === 'error'
      ? theme.palette.error
      : theme.palette.text;

  const bg =
    tone === 'text'
      ? alpha(theme.palette.text.primary, isDark ? 0.1 : 0.06)
      : alpha(pal.main, isDark ? 0.14 : 0.1);

  const bd =
    tone === 'text'
      ? alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1)
      : alpha(pal.main, 0.18);

  const fg = tone === 'text' ? theme.palette.text.primary : pal.main;

  return (
    <MDBox
      sx={{
        px: 0.75,
        py: 0.2,
        borderRadius: '999px',
        fontSize: '0.72rem',
        fontWeight: 900,
        lineHeight: 1.2,
        bgcolor: bg,
        border: `1px solid ${bd}`,
        color: fg,
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </MDBox>
  );
}

// ---------- component ----------
export function ScheduleTab({
  tab,
  memberId,
  limit = 150,
  onOpenSession, // (sessionId) => void
}) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [filter, setFilter] = useState('upcoming'); // upcoming | past | all
  const [openMonth, setOpenMonth] = useState(null);
  const [openItemKey, setOpenItemKey] = useState(null);

  useEffect(() => {
    let alive = true;
    const isActive = tab === 4; // ✅ ScheduleTab index 4 varsaydım (senin modalda kaçıncı tabsa bunu değiştir)
    if (!isActive) return;
    if (!memberId) return;

    (async () => {
      setLoading(true);
      setErr(null);
      try {
        // ✅ timeline: from/to opsiyonel. Şimdilik ikisini de null bırakıp limit ile getiriyoruz.
        const data = await sessionsApi.getMemberTimeline(memberId, { limit });
        if (!alive) return;

        const arr = Array.isArray(data) ? data : [];

        // newest first (dayISO desc, time desc)
        arr.sort((a, b) => {
          const da = String(a?.dayISO || '');
          const db = String(b?.dayISO || '');
          if (db !== da) return db.localeCompare(da);
          const sa = Number(a?.startMin ?? 0);
          const sb = Number(b?.startMin ?? 0);
          if (sb !== sa) return sb - sa;
          return Number(b?.sessionId ?? 0) - Number(a?.sessionId ?? 0);
        });

        setRows(arr);

        const first = arr[0];
        const mk = monthKey(first?.dayISO);
        setOpenMonth(mk !== '—' ? mk : null);
      } catch (e) {
        if (!alive) return;
        console.error('[ScheduleTab] getMemberTimeline failed', e);
        setRows([]);
        setErr('Seans planı yüklenemedi.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [tab, memberId, limit]);

  const filteredRows = useMemo(() => {
    const arr = Array.isArray(rows) ? rows : [];
    if (filter === 'all') return arr;

    const today = todayISO_TR();
    if (filter === 'upcoming') {
      // today ve sonrası
      return arr.filter((r) => String(r?.dayISO || '') >= today);
    }
    // past: dünden önce + today’de ama saat geçmiş gibi detaylı ayrım yok; basit dayISO ile.
    return arr.filter((r) => String(r?.dayISO || '') < today);
  }, [rows, filter]);

  const months = useMemo(() => {
    const arr = Array.isArray(filteredRows) ? filteredRows : [];
    const map = new Map(); // monthKey -> rows[]
    for (const r of arr) {
      const mk = monthKey(r?.dayISO);
      if (!map.has(mk)) map.set(mk, []);
      map.get(mk).push(r);
    }
    const keys = Array.from(map.keys()).sort((a, b) => String(b).localeCompare(String(a)));

    return keys.map((k) => {
      const list = (map.get(k) || []).slice().sort((a, b) => {
        // day desc, time desc (kısa bakışta daha mantıklı)
        const da = String(a?.dayISO || '');
        const db = String(b?.dayISO || '');
        if (db !== da) return db.localeCompare(da);
        return Number(b?.startMin ?? 0) - Number(a?.startMin ?? 0);
      });
      const uniqueDays = new Set(list.map((x) => x?.dayISO).filter(Boolean));
      return { month: k, label: monthLabelTR(k), list, dayCount: uniqueDays.size };
    });
  }, [filteredRows]);

  const totalCount = Array.isArray(filteredRows) ? filteredRows.length : 0;

  return (
    <TabPanel value={tab} index={4}>
      <SoftCard>
        {/* Header */}
        <MDBox display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <PillIcon icon="event" tone="teal" />
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                Seans Planı
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Üyenin geçmiş + gelecek seansları • Compact accordion
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
            {loading ? (
              <MDBox sx={{ display: 'grid', placeItems: 'center', width: 28, height: 28 }}>
                <CircularProgress size={18} />
              </MDBox>
            ) : null}
            <Chip
              size="small"
              label={`${totalCount} kayıt`}
              variant="outlined"
              sx={{ borderRadius: '999px', fontWeight: 900 }}
            />
          </MDBox>
        </MDBox>

        {/* Filter chips */}
        <MDBox sx={{ display: 'flex', gap: 0.7, flexWrap: 'wrap', mb: 1 }}>
          {[
            ['upcoming', 'Gelecek', 'schedule'],
            ['past', 'Geçmiş', 'history'],
            ['all', 'Hepsi', 'view_list'],
          ].map(([k, label, icon]) => {
            const active = filter === k;
            return (
              <Chip
                key={k}
                icon={<Icon sx={{ fontSize: 18 }}>{icon}</Icon>}
                label={label}
                size="small"
                variant={active ? 'filled' : 'outlined'}
                onClick={() => setFilter(k)}
                sx={{
                  borderRadius: '999px',
                  fontWeight: 900,
                  bgcolor: active
                    ? alpha(theme.palette.info.main, isDark ? 0.18 : 0.12)
                    : 'transparent',
                  borderColor: alpha(theme.palette.info.main, active ? 0.22 : 0.12),
                }}
              />
            );
          })}
        </MDBox>

        {err ? (
          <MDTypography variant="caption" sx={{ color: 'error.main', fontWeight: 800, mb: 1 }}>
            {err}
          </MDTypography>
        ) : null}

        {!loading && months.length === 0 ? (
          <MDBox
            sx={{
              p: 1,
              borderRadius: '14px',
              border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.12)}`,
              bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.9),
            }}
          >
            <MDTypography variant="button" sx={{ opacity: 0.75, fontWeight: 800 }}>
              Kayıt yok
            </MDTypography>
          </MDBox>
        ) : null}

        {/* Months accordion */}
        <MDBox sx={{ display: 'grid', gap: 0.75 }}>
          {months.map(({ month, label, list, dayCount }) => {
            const expanded = openMonth === month;

            return (
              <Accordion
                key={month}
                expanded={expanded}
                onChange={() => setOpenMonth(expanded ? null : month)}
                disableGutters
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.12)}`,
                  bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.92),
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                  sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.8 } }}
                >
                  <MDBox
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: 1,
                    }}
                  >
                    <MDBox sx={{ display: 'grid', gap: 0.1 }}>
                      <MDTypography variant="button" fontWeight="bold">
                        {label}
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ opacity: 0.7, fontWeight: 800 }}>
                        {dayCount} gün • {list.length} seans
                      </MDTypography>
                    </MDBox>

                    {smallPill(theme, isDark, `${list.length}`, 'info')}
                  </MDBox>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                  <MDBox sx={{ display: 'grid', gap: 0.45 }}>
                    {list.map((r) => {
                      const dayISO = r?.dayISO || '—';
                      const dayLbl = dayLabelTR(dayISO);
                      const time = hmFromMin(r?.startMin);

                      const activity = r?.activityName || '—';
                      const trainer = r?.trainerName || '—';

                      const ss = sessionStatusMeta(r?.sessionStatus, theme, isDark);
                      const att = attendanceMeta(r?.attendanceStatus, theme, isDark);

                      const planName = String(r?.planName || '').trim();
                      const planStart = r?.planStartAt || '—';
                      const planEnd = r?.planEstimatedEndAt || '—';

                      const ord = r?.packageSessionOrdinal ?? null;
                      const total = r?.packageTotalSessions ?? null;
                      const nth = ord && total ? `${ord}/${total}` : ord ? `${ord}/—` : '—/—';

                      const billingMode = String(r?.billingMode || '').toUpperCase();
                      const sessionType = String(r?.sessionType || '').toUpperCase();
                      const typeLabel =
                        sessionType === 'TELAFI' ? 'Telafi' : sessionType ? sessionType : '—';
                      const billLabel =
                        billingMode === 'PACKAGE'
                          ? 'Paket'
                          : billingMode === 'PAID'
                          ? 'Ücretli'
                          : billingMode || '—';

                      const itemKey =
                        r?.sessionParticipantId ?? r?.sessionId ?? `${dayISO}-${r?.startMin}`;
                      const isOpen = openItemKey === itemKey;

                      const clickable = Boolean(onOpenSession && r?.sessionId);

                      const noteCount = Number(r?.noteCount ?? 0);
                      const hasReminder = Boolean(r?.hasReminder);

                      const participantNote = String(r?.participantNote || '').trim();
                      const hasParticipantNote = Boolean(participantNote);

                      return (
                        <Accordion
                          key={itemKey}
                          expanded={isOpen}
                          onChange={() => setOpenItemKey(isOpen ? null : itemKey)}
                          disableGutters
                          elevation={0}
                          sx={{
                            borderRadius: '14px',
                            overflow: 'hidden',
                            border: `1px solid ${alpha(
                              theme.palette.text.primary,
                              isDark ? 0.12 : 0.1
                            )}`,
                            bgcolor: isDark ? alpha('#0b1220', 0.14) : alpha('#0b1220', 0.02),
                            '&:before': { display: 'none' },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<Icon sx={{ fontSize: 18 }}>chevron_right</Icon>}
                            sx={{
                              minHeight: 42,
                              '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                                transform: 'rotate(90deg)',
                              },
                              '& .MuiAccordionSummary-content': { my: 0.6 },
                              px: 1.0,
                            }}
                          >
                            <MDBox
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                gap: 1,
                              }}
                            >
                              {/* left compact */}
                              <MDBox
                                sx={{
                                  minWidth: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.7,
                                }}
                              >
                                <MDTypography variant="button" sx={{ fontWeight: 900 }} noWrap>
                                  {dayLbl} {dayISO} • {time}
                                </MDTypography>

                                {/* session status */}
                                <MDBox
                                  sx={{
                                    px: 0.65,
                                    py: 0.16,
                                    borderRadius: '999px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.45,
                                    bgcolor: ss.bg,
                                    border: `1px solid ${ss.bd}`,
                                    flex: '0 0 auto',
                                  }}
                                >
                                  <Icon sx={{ fontSize: 15, color: ss.fg }}>{ss.icon}</Icon>
                                  <MDTypography
                                    variant="caption"
                                    sx={{ fontWeight: 900, color: ss.fg }}
                                    noWrap
                                  >
                                    {ss.label}
                                  </MDTypography>
                                </MDBox>

                                {/* attendance */}
                                <MDBox
                                  sx={{
                                    px: 0.65,
                                    py: 0.16,
                                    borderRadius: '999px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.45,
                                    bgcolor: att.bg,
                                    border: `1px solid ${att.bd}`,
                                    flex: '0 0 auto',
                                  }}
                                >
                                  <Icon sx={{ fontSize: 15, color: att.fg }}>{att.icon}</Icon>
                                  <MDTypography
                                    variant="caption"
                                    sx={{ fontWeight: 900, color: att.fg }}
                                    noWrap
                                  >
                                    {att.label}
                                  </MDTypography>
                                </MDBox>

                                {smallPill(theme, isDark, billLabel, 'text')}
                                {smallPill(theme, isDark, typeLabel, 'text')}

                                <Tooltip title={`${activity} • ${trainer}`} arrow placement="top">
                                  <MDTypography
                                    variant="caption"
                                    sx={{ fontWeight: 900, opacity: 0.78, maxWidth: 260 }}
                                    noWrap
                                  >
                                    {activity} • {trainer}
                                  </MDTypography>
                                </Tooltip>

                                {planName ? (
                                  <Tooltip
                                    title={`${planName}\n${planStart} → ${planEnd}`}
                                    arrow
                                    placement="top"
                                  >
                                    <MDTypography
                                      variant="caption"
                                      sx={{ fontWeight: 900, opacity: 0.78, maxWidth: 220 }}
                                      noWrap
                                    >
                                      {planName}
                                    </MDTypography>
                                  </Tooltip>
                                ) : (
                                  <MDTypography
                                    variant="caption"
                                    sx={{ fontWeight: 900, opacity: 0.55 }}
                                    noWrap
                                  >
                                    Paket: —
                                  </MDTypography>
                                )}

                                {smallPill(theme, isDark, `#${nth}`, 'success')}
                              </MDBox>

                              {/* right small icons */}
                              <MDBox
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.45,
                                  flex: '0 0 auto',
                                }}
                              >
                                {hasReminder ? (
                                  <Tooltip title="Hatırlatma var" arrow>
                                    <MDBox
                                      sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: '10px',
                                        display: 'grid',
                                        placeItems: 'center',
                                        bgcolor: alpha(
                                          theme.palette.info.main,
                                          isDark ? 0.14 : 0.1
                                        ),
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                                      }}
                                    >
                                      <Icon sx={{ fontSize: 16, color: theme.palette.info.main }}>
                                        notifications
                                      </Icon>
                                    </MDBox>
                                  </Tooltip>
                                ) : null}

                                {noteCount > 0 ? (
                                  <Tooltip title={`${noteCount} not`} arrow>
                                    <MDBox
                                      sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: '10px',
                                        display: 'grid',
                                        placeItems: 'center',
                                        bgcolor: alpha(
                                          theme.palette.warning.main,
                                          isDark ? 0.16 : 0.12
                                        ),
                                        border: `1px solid ${alpha(
                                          theme.palette.warning.main,
                                          0.22
                                        )}`,
                                      }}
                                    >
                                      <Icon
                                        sx={{ fontSize: 16, color: theme.palette.warning.main }}
                                      >
                                        sticky_note_2
                                      </Icon>
                                    </MDBox>
                                  </Tooltip>
                                ) : null}

                                {clickable ? (
                                  <Tooltip title="Seansı aç" arrow>
                                    <MDBox
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenSession(r.sessionId);
                                      }}
                                      sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: '10px',
                                        display: 'grid',
                                        placeItems: 'center',
                                        bgcolor: alpha(
                                          theme.palette.success.main,
                                          isDark ? 0.14 : 0.1
                                        ),
                                        border: `1px solid ${alpha(
                                          theme.palette.success.main,
                                          0.18
                                        )}`,
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <Icon
                                        sx={{ fontSize: 16, color: theme.palette.success.main }}
                                      >
                                        open_in_new
                                      </Icon>
                                    </MDBox>
                                  </Tooltip>
                                ) : null}
                              </MDBox>
                            </MDBox>
                          </AccordionSummary>

                          <AccordionDetails sx={{ px: 1.0, pt: 0, pb: 0.9 }}>
                            <Divider sx={{ opacity: 0.35, mb: 0.8 }} />

                            <MDBox
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                gap: 0.9,
                              }}
                            >
                              {/* Left */}
                              <MDBox sx={{ display: 'grid', gap: 0.35 }}>
                                <MDTypography
                                  variant="caption"
                                  sx={{ opacity: 0.75, fontWeight: 800 }}
                                >
                                  Paket Bilgisi
                                </MDTypography>

                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 900, opacity: 0.86 }}
                                >
                                  {planName ? planName : '—'}
                                </MDTypography>

                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 900, opacity: 0.78 }}
                                >
                                  {`Tarih: ${planStart} → ${planEnd}`}
                                </MDTypography>

                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 900, opacity: 0.78 }}
                                >
                                  {`Seans: ${nth} • Consumed: ${r?.consumedCount ?? '—'}`}
                                </MDTypography>

                                <MDTypography
                                  variant="caption"
                                  sx={{ opacity: 0.75, fontWeight: 800, mt: 0.6 }}
                                >
                                  Katılımcı Notu
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 800, opacity: hasParticipantNote ? 0.9 : 0.55 }}
                                >
                                  {hasParticipantNote ? participantNote : '—'}
                                </MDTypography>
                              </MDBox>

                              {/* Right */}
                              <MDBox sx={{ display: 'grid', gap: 0.35 }}>
                                <MDTypography
                                  variant="caption"
                                  sx={{ opacity: 0.75, fontWeight: 800 }}
                                >
                                  Ödeme / Ücret (Ücretli ise)
                                </MDTypography>

                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 900, opacity: 0.82 }}
                                >
                                  {`Fiyat: ${r?.priceTry ?? '—'} ₺ • Durum: ${
                                    r?.paymentStatus ?? '—'
                                  }`}
                                </MDTypography>

                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 900, opacity: 0.82 }}
                                >
                                  {`Toplam: ${r?.totalTry ?? '—'} • Ödenen: ${
                                    r?.paidTry ?? '—'
                                  } • Bekleyen: ${r?.pendingTry ?? '—'} • Borç: ${
                                    r?.debtTry ?? '—'
                                  }`}
                                </MDTypography>

                                <MDTypography
                                  variant="caption"
                                  sx={{ opacity: 0.75, fontWeight: 800, mt: 0.6 }}
                                >
                                  Notlar / Hatırlatma
                                </MDTypography>

                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 900, opacity: 0.82 }}
                                >
                                  {`Not: ${noteCount} • Hatırlatma: ${hasReminder ? 'Var' : 'Yok'}`}
                                </MDTypography>

                                <MDTypography
                                  variant="caption"
                                  sx={{ opacity: 0.75, fontWeight: 800, mt: 0.6 }}
                                >
                                  Seans Snapshot
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 900, opacity: 0.82 }}
                                >
                                  {`Billing: ${billLabel} • Type: ${typeLabel} • Status: ${ss.label}`}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </MDBox>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </MDBox>
      </SoftCard>
    </TabPanel>
  );
}
